import { useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Collapse, Empty, Flex, Input, Popconfirm, Segmented, Select, Skeleton, Space, Table, Tabs, Tag, Tooltip, Typography, message } from 'antd';
import { useSearchParams } from 'react-router';
import { UnifiedList } from './UnifiedList';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, type Me } from '../../app/api-context';
import { useSession } from '../../app/session-context';
import { VocabEditModal } from './VocabEditModal';
import { VocabCreateModal } from './VocabCreateModal';
import { GenerationRequestModal } from './GenerationRequestModal';
import { ManualModal, type ManualSource } from '../images/ManualModal';
import { CATEGORY_LABEL, FIELD_LABEL, PROGRESS_LABEL, PURPOSE_LABEL, candidateKey, purposeOf, themeMatches, themeOptions, matchesQuery, type ApplyDefaultsResult, type ImportPreview, type ImportResult, type VocabDraft, type VocabItem, type VocabProgress, type VocabPurpose } from '../../lib/vocab-types';

type TypeFilter = 'all' | 'core' | 'fringe';

const typeTag = (t: 'core' | 'fringe') => (t === 'core' ? <Tag color="blue">핵심</Tag> : <Tag>주변</Tag>);
const slugCode = (s: string | null) => (s ? <Typography.Text code>{s}</Typography.Text> : <Typography.Text type="warning">없음</Typography.Text>);
const keywordTags = (k: string[]) => (k.length ? k.map((x) => <Tag key={x} style={{ marginInlineEnd: 4 }}>{x}</Tag>) : <Typography.Text type="secondary">-</Typography.Text>);

function ImportPanel({ preview, canWrite }: { preview: ImportPreview; canWrite: boolean }) {
  const api = useApi();
  const qc = useQueryClient();
  const [msg, holder] = message.useMessage();
  const [selected, setSelected] = useState<string[]>([]);
  const [fillPicked, setFillPicked] = useState<string[]>([]);
  const [theme, setTheme] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (keys: string[]) => api.post<ImportResult>('/vocab/import', { keys }),
    onSuccess: (r) => {
      msg.success(`어휘 ${r.created}건을 저장했습니다${r.ids.length ? ` (${r.ids[0]} ~ ${r.ids[r.ids.length - 1]})` : ''}.`);
      setSelected([]);
      void qc.invalidateQueries({ queryKey: ['vocab'] });
    },
  });
  const fill = useMutation({
    mutationFn: (vocabIds: string[]) => api.post<ApplyDefaultsResult>('/vocab/apply-defaults', { vocabIds }),
    onSuccess: (r) => {
      msg.success(`기본값을 ${r.updated.length}건에 채웠습니다${r.skipped.length ? ` (건너뜀 ${r.skipped.length}건: ${r.skipped.map((x) => `${x.vocabId} ${x.reason}`).join(' / ')})` : ''}.`);
      setFillPicked([]);
      void qc.invalidateQueries({ queryKey: ['vocab'] });
    },
  });
  const fillable = preview.existing.filter((e) => e.fill);
  const needsSlug = preview.needsSlug ?? [];
  // 채우기로 해결되는 차이(빈 슬러그·빠진 키워드)는 따로 보여준다
  const changed = preview.existing
    .map((e) => ({ ...e, differences: e.differences.filter((d) => !(d === 'slug' && e.fill?.slug) && !(d === 'keywords' && e.fill?.keywords)) }))
    .filter((e) => e.differences.length > 0);
  if (!preview.toCreate.length && !preview.errors.length && !changed.length && !fillable.length && !needsSlug.length) return null;

  return (
    <Card size="small" title="후보 어휘 가져오기" style={{ marginBottom: 16 }}>
      {holder}
      <Space direction="vertical" style={{ width: '100%' }}>
        {preview.errors.length > 0 && (
          <Alert type="error" showIcon message={`후보 CSV 오류 ${preview.errors.length}건 — 고칠 때까지 저장할 수 없습니다`} description={preview.errors.map((e) => `${e.line}행: ${e.message}`).join(' / ')} />
        )}
        {preview.toCreate.length > 0 && (
          <Alert
            type="info"
            showIcon
            message={`후보 CSV(data/vocab/vocab-candidates.csv)에 아직 저장하지 않은 어휘 ${preview.toCreate.length}건이 있습니다.`}
            description={`필요한 어휘만 골라 저장하세요. 고르지 않은 후보는 그대로 남아 나중에 저장할 수 있습니다. 이미 저장된 어휘 ${preview.existing.length}건은 바꾸지 않습니다.${preview.mergedAsKeywords.length ? ` 유아어·반말은 대표 어휘를 저장할 때 키워드로 함께 들어갑니다(${preview.mergedAsKeywords.map((m) => `${m.word}→${m.into}`).join(', ')}).` : ''}`}
          />
        )}
        {needsSlug.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`동음이의어 후보 ${needsSlug.length}건 보류 — 같은 표기의 저장 어휘에 슬러그가 없어 어느 뜻인지 알 수 없습니다`}
            description={`저장된 어휘(${[...new Set(needsSlug.flatMap((n) => n.vocabIds))].join(', ')})를 수정해 슬러그를 먼저 정하면 나머지 뜻을 새 어휘로 저장할 수 있습니다. 보류: ${needsSlug.map((n) => `${n.text}(${n.key})`).join(', ')}`}
          />
        )}
        {fillable.length > 0 && (
          <Collapse
            size="small"
            defaultActiveKey={['fill']}
            items={[{
              key: 'fill',
              label: `저장된 어휘에 기본값 채우기 (${fillable.length}건, 선택 ${fillPicked.length}건)`,
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    후보 CSV의 기본 슬러그·키워드 중 비어 있는 슬러그와 빠진 키워드를 채웁니다. 게시된 어휘의 슬러그는 채우지 않습니다. 기본 카드 선정(D-V11)에 따라 용도·우선순위가 다른 어휘는 바꿀 값을 함께 보여줍니다(고른 어휘만 바뀝니다).
                  </Typography.Text>
                  <Table<ImportPreview['existing'][number]>
                    size="small"
                    rowKey="vocabId"
                    rowSelection={{ selectedRowKeys: fillPicked, onChange: (keys) => setFillPicked(keys as string[]), getCheckboxProps: () => ({ disabled: !canWrite }) }}
                    pagination={{ pageSize: 100, hideOnSinglePage: true }}
                    dataSource={fillable}
                    columns={[
                      { title: 'ID', dataIndex: 'vocabId', width: 80 },
                      { title: '표기', dataIndex: 'text' },
                      { title: '채울 슬러그', render: (_, e) => (e.fill?.slug ? <Typography.Text code>{e.fill.slug}</Typography.Text> : '-') },
                      { title: '채운 뒤 키워드', render: (_, e) => (e.fill?.keywords ? keywordTags(e.fill.keywords) : '-') },
                      { title: '바꿀 용도', render: (_, e) => (e.fill?.purpose ? <Tag color={PURPOSE_LABEL[e.fill.purpose].color}>{PURPOSE_LABEL[e.fill.purpose].label}</Tag> : '-') },
                      { title: '바꿀 우선순위', render: (_, e) => e.fill?.priority ?? '-' },
                    ]}
                  />
                  {fill.isError && <Alert type="error" showIcon message="채우지 못했습니다" description={(fill.error as Error).message} />}
                  <Flex justify="flex-end">
                    <Popconfirm
                      title={`선택한 어휘 ${fillPicked.length}건에 기본값을 채울까요?`}
                      description="운영 Firestore admin_vocab의 슬러그·키워드가 바뀌고 감사 기록이 남습니다."
                      okText="채우기"
                      cancelText="취소"
                      disabled={!canWrite || preview.errors.length > 0 || !fillPicked.length}
                      onConfirm={() => fill.mutate(fillPicked)}
                    >
                      <Button disabled={!canWrite || preview.errors.length > 0 || !fillPicked.length} loading={fill.isPending}>
                        선택한 {fillPicked.length}건 채우기
                      </Button>
                    </Popconfirm>
                  </Flex>
                </Space>
              ),
            }]}
          />
        )}
        {changed.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`저장된 어휘와 후보 CSV가 다른 어휘 ${changed.length}건(자동으로 바꾸지 않음)`}
            description={changed.map((c) => `${c.text}(${c.vocabId}): ${c.differences.map((d) => FIELD_LABEL[d] ?? d).join(', ')}`).join(' / ')}
          />
        )}
        {preview.toCreate.length > 0 && (
          <>
            <Collapse
              defaultActiveKey={['list']}
              size="small"
              items={[{
                key: 'list',
                label: `저장할 어휘 고르기 (후보 ${preview.toCreate.length}건, 선택 ${selected.length}건)`,
                children: (
                  <>
                  <Flex gap={8} align="center" style={{ marginBottom: 8 }}>
                    <Select allowClear placeholder="주제" aria-label="후보 주제" value={theme} onChange={(v) => setTheme(v ?? null)} style={{ width: 240 }} options={themeOptions(preview.toCreate)} />
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>주제별 후보는 연구 근거가 아닌 기획 초안입니다(D-V10). 필요한 것만 골라 저장하세요.</Typography.Text>
                  </Flex>
                  <Table<VocabDraft>
                    size="small"
                    rowKey={candidateKey}
                    rowSelection={{ selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]), getCheckboxProps: () => ({ disabled: !canWrite }) }}
                    pagination={{ pageSize: 100, hideOnSinglePage: true }}
                    dataSource={preview.toCreate.filter((d) => themeMatches(d, theme))}
                    columns={[
                      { title: '표기', dataIndex: 'text' },
                      { title: '주제', dataIndex: 'theme', render: (t: string | null | undefined) => t ?? '-' },
                      { title: '슬러그', dataIndex: 'slug', render: slugCode },
                      { title: '카테고리', dataIndex: 'category', render: (c: string) => CATEGORY_LABEL[c] ?? c },
                      { title: '구분', dataIndex: 'vocabType', render: typeTag },
                      { title: '용도', key: 'purpose', render: (_: unknown, v: VocabDraft) => { const p = PURPOSE_LABEL[purposeOf(v)]; return <Tag color={p.color}>{p.label}</Tag>; } },
                      { title: '키워드', dataIndex: 'keywords', render: keywordTags },
                      { title: '기존 카드', dataIndex: 'existingCardId', render: (v: string | null) => v ?? '-' },
                      { title: '출처', dataIndex: 'sources', render: (s: VocabDraft['sources']) => s.map((x) => x.sourceId).join(', ') || '-' },
                    ]}
                  />
                  </>
                ),
              }]}
            />
            {run.isError && <Alert type="error" showIcon message="저장하지 못했습니다" description={(run.error as Error).message} />}
            <Flex justify="flex-end">
              <Tooltip title={canWrite ? (selected.length ? '' : '저장할 어휘를 먼저 고르세요.') : '저장은 editor 권한이 필요합니다.'}>
                <Popconfirm
                  title={`선택한 어휘 ${selected.length}건을 저장할까요?`}
                  description="운영 Firestore admin_vocab에 새 문서로 추가됩니다. 기존 어휘는 바꾸지 않습니다."
                  okText="저장"
                  cancelText="취소"
                  disabled={!canWrite || preview.errors.length > 0 || !selected.length}
                  onConfirm={() => run.mutate(selected)}
                >
                  <Button type="primary" disabled={!canWrite || preview.errors.length > 0 || !selected.length} loading={run.isPending}>
                    선택한 {selected.length}건 저장
                  </Button>
                </Popconfirm>
              </Tooltip>
            </Flex>
          </>
        )}
      </Space>
    </Card>
  );
}

export function VocabPage() {
  const api = useApi();
  const { user } = useSession();
  const me = useQuery({ queryKey: ['me', user?.email ?? null], queryFn: () => api.get<Me>('/me'), staleTime: 5 * 60_000, retry: false });
  const list = useQuery({ queryKey: ['vocab', 'list'], queryFn: () => api.get<{ items: VocabItem[] }>('/vocab'), retry: false });
  const preview = useQuery({ queryKey: ['vocab', 'import-preview'], queryFn: () => api.get<ImportPreview>('/vocab/import-preview'), retry: false });

  const [q, setQ] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [type, setType] = useState<TypeFilter>('all');
  const [progress, setProgress] = useState<VocabProgress | null>(null);
  const [noSlug, setNoSlug] = useState(false);
  const [purpose, setPurpose] = useState<VocabPurpose | null>(null);
  const [creating, setCreating] = useState(false);
  const [createdMsg, createdHolder] = message.useMessage();
  const [editing, setEditing] = useState<VocabItem | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [requesting, setRequesting] = useState(false);
  // 통합 목록에서 요청한 생성 대상(관리 어휘로 저장된 뒤의 ID). null이면 '관리 중 어휘' 탭의 선택
  const [genIds, setGenIds] = useState<string[] | null>(null);
  const [manual, setManual] = useState<ManualSource | null>(null);
  const canWrite = me.data?.role === 'editor';
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'manage' ? 'manage' : 'unified';

  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  const filtered = useMemo(
    () => items.filter((v) => (!category || v.category === category) && (type === 'all' || v.vocabType === type) && (!progress || v.progress === progress) && (!noSlug || !v.slug) && (!purpose || purposeOf(v) === purpose) && matchesQuery(v, q)),
    [items, category, type, progress, noSlug, purpose, q],
  );

  return (
    <>
      {createdHolder}
      <Flex justify="space-between" align="center" wrap gap={8}>
        <Typography.Title level={3}>어휘 관리</Typography.Title>
        {canWrite && <Button type="primary" onClick={() => setCreating(true)}>어휘 추가</Button>}
      </Flex>
      <Typography.Paragraph type="secondary">
        한국어 AAC 핵심·주변어휘와 이미지 구축 현황입니다. '통합 목록'은 추가할 후보, 관리 중 어휘, 앱에 이미 등록된 기본 카드·검색 이미지를 어휘 하나에 한 줄로 보여줍니다. 제작 완료는 이미지 게시 성공을 기준으로 표시합니다.
      </Typography.Paragraph>

      {preview.data && <ImportPanel preview={preview.data} canWrite={canWrite} />}
      {preview.isError && <Alert type="error" showIcon style={{ marginBottom: 16 }} message="후보 어휘를 확인하지 못했습니다" description={(preview.error as Error).message} />}

      <Tabs
        activeKey={view}
        onChange={(k) => setParams((p) => { const n = new URLSearchParams(p); if (k === 'manage') n.set('view', 'manage'); else n.delete('view'); return n; }, { replace: true })}
        items={[
          {
            key: 'unified',
            label: '통합 목록',
            children: (
              <UnifiedList
                canWrite={canWrite}
                onEdit={(id) => setEditing(items.find((v) => v.vocabId === id) ?? null)}
                onManual={(vocabId, text, asset) => setManual({
                  vocabId, text,
                  asset: asset ? { assetId: asset.assetId, prompt: null, promptParts: null, modelId: null, previewUrl: asset.previewUrl, hasOriginal: true, preferReference: true } : null,
                })}
                onGenerate={(ids) => { setGenIds(ids); setRequesting(true); }}
              />
            ),
          },
          {
            key: 'manage',
            label: `관리 중 어휘(제작) ${items.length}`,
            children: (
              <Card size="small">
                <Flex wrap gap={8} style={{ marginBottom: 12 }}>
                  <Input.Search allowClear placeholder="표기·키워드·ID 검색" onSearch={setQ} onChange={(e) => !e.target.value && setQ('')} style={{ width: 240 }} />
                  <Select allowClear placeholder="카테고리" value={category} onChange={(v) => setCategory(v ?? null)} style={{ width: 140 }} options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
                  <Segmented<TypeFilter> value={type} onChange={setType} options={[{ value: 'all', label: '전체' }, { value: 'core', label: '핵심' }, { value: 'fringe', label: '주변' }]} />
                  <Select allowClear placeholder="진행 상태" value={progress} onChange={(v) => setProgress(v ?? null)} style={{ width: 160 }} options={Object.entries(PROGRESS_LABEL).map(([value, p]) => ({ value, label: p.label }))} />
                  <Select allowClear placeholder="용도" value={purpose} onChange={(v) => setPurpose(v ?? null)} style={{ width: 150 }} options={(Object.keys(PURPOSE_LABEL) as VocabPurpose[]).map((p) => ({ value: p, label: PURPOSE_LABEL[p].label }))} />
                  <Checkbox checked={noSlug} onChange={(e) => setNoSlug(e.target.checked)} style={{ alignSelf: 'center' }}>슬러그 없음</Checkbox>
                  <Typography.Text type="secondary" style={{ alignSelf: 'center' }}>{filtered.length} / {items.length}건</Typography.Text>
                  {canWrite && (
                    <Button type="primary" disabled={!picked.length} onClick={() => { setGenIds(null); setRequesting(true); }} style={{ marginInlineStart: 'auto' }}>
                      이미지 생성 요청{picked.length ? ` (${picked.length})` : ''}
                    </Button>
                  )}
                </Flex>
                {list.isPending && <Skeleton active />}
                {list.isError && <Alert type="error" showIcon message="어휘 목록을 불러오지 못했습니다" description={(list.error as Error).message} />}
                {list.data && (items.length === 0 ? (
                  <Empty description="저장된 어휘가 없습니다. 위에서 후보 어휘를 가져오세요." />
                ) : (
                  <Table<VocabItem>
                    size="small"
                    rowKey="vocabId"
                    rowSelection={canWrite ? { selectedRowKeys: picked, onChange: (keys) => setPicked(keys as string[]), preserveSelectedRowKeys: true } : undefined}
                    dataSource={filtered}
                    pagination={{ pageSize: 50, hideOnSinglePage: true }}
                    columns={[
                      { title: 'ID', dataIndex: 'vocabId', width: 80 },
                      { title: '표기', dataIndex: 'text', render: (t: string, v) => <>{t}{v.sourceWord && <Typography.Text type="secondary" style={{ fontSize: 12 }}> ({v.sourceWord})</Typography.Text>}</> },
                      { title: '카테고리', dataIndex: 'category', render: (c: string) => CATEGORY_LABEL[c] ?? c },
                      { title: '구분', dataIndex: 'vocabType', render: typeTag },
                      { title: '용도', key: 'purpose', render: (_: unknown, v: VocabItem) => { const p = PURPOSE_LABEL[purposeOf(v)]; return <Tag color={p.color}>{p.label}</Tag>; } },
                      { title: '키워드', dataIndex: 'keywords', render: keywordTags },
                      { title: '기존 카드', dataIndex: 'existingCardId', render: (c: string | null) => (c ? <Typography.Text code>{c}</Typography.Text> : '-') },
                      { title: '슬러그', dataIndex: 'slug', render: slugCode },
                      { title: '우선순위', dataIndex: 'priority', width: 80 },
                      { title: '진행 상태', dataIndex: 'progress', render: (p: VocabProgress) => <Tag color={PROGRESS_LABEL[p].color}>{PROGRESS_LABEL[p].label}</Tag> },
                      { title: '게시(기본/검색)', render: (_, v) => `${v.stats.defaultPublished} / ${v.stats.searchPublished}` },
                      { title: '출처', dataIndex: 'sources', render: (s: VocabItem['sources'], v) => <Tooltip title={v.rationale || undefined}>{s.map((x) => x.sourceId).join(', ') || '-'}</Tooltip> },
                      ...(canWrite ? [{ title: '', key: 'edit', width: 150, render: (_: unknown, v: VocabItem) => (
                        <Flex gap={4}>
                          <Button size="small" onClick={() => setEditing(v)}>수정</Button>
                          <Button size="small" disabled={v.imageType !== 'realistic'} onClick={() => setManual({ vocabId: v.vocabId, text: v.text, asset: null })}>수동 제작</Button>
                        </Flex>
                      ) }] : []),
                    ]}
                  />
                ))}
              </Card>
            ),
          },
        ]}
      />
      <VocabEditModal vocab={editing} onClose={() => setEditing(null)} />
      <VocabCreateModal
        open={creating}
        existing={items}
        onClose={() => setCreating(false)}
        onCreated={(ids, generate) => {
          setCreating(false);
          createdMsg.success(`어휘 ${ids.length}건을 추가했습니다 (${ids[0]}${ids.length > 1 ? ` ~ ${ids[ids.length - 1]}` : ''}).`);
          if (generate) { setGenIds(ids); setRequesting(true); }
        }}
      />
      <ManualModal source={manual} onClose={() => setManual(null)} />
      <GenerationRequestModal open={requesting} vocab={items.filter((v) => (genIds ?? picked).includes(v.vocabId))} onClose={() => { setRequesting(false); setGenIds(null); }} />
    </>
  );
}
